// Phones (iOS above all) kill a tab that holds too many decoded images: with every scene's art
// prefetched the game kept ~460 MB of textures (x2 on iOS: the decoded image and its GPU copy),
// and the farm's art pushed Chrome / Safari on an iPhone over the edge ("Can't open this page"
// while tapping around the farm, owner 2026-09-30). Most of it is scene backgrounds — every
// scene x phase x house level at 1672x941 or wider — and the board-game boards. On touch
// devices the background loader skips those for scenes you are not in or next to, and a scene
// change frees the ones no longer needed. Only this family is freed: every scene declares its
// own background (needsFor), so it is always fetched back when needed.
//
// That still left ~300 MB queued on the login form (owner's mom, 2026-10-24: an iPhone in the
// Google app crashed in a loop right after the download, "Nie można uzyskać dostępu do
// witryny"). So on a phone the login form fetches only the yard itself and the music, tier 1
// brings the places next door as they look now (assetManifest `tiers(…, { lean })`), and the
// last sweep keeps only the sounds and the small pictures: big ones are loaded by whoever
// draws them (a scene's needsFor, the quiz, whenReady on demand).
export const BIG_PX = 1_000_000;                        // ~4 MB decoded
export const SMALL_PX = 131_072;                        // 0.5 MB decoded: 323 of the sweep's pictures, ~34 MB together
export const isBigFamily = (key) => key.startsWith('bg_') || key.endsWith('_board');

/** Tiers 0-1 as they are; the sweep keeps sounds and pictures under SMALL_PX (`pixels`: key -> w*h). */
export function leanTiers([t0, t1, t2], pixels) {
  const small = (k) => k.startsWith('audio:') || (!isBigFamily(k) && (pixels[k] ?? Infinity) < SMALL_PX);
  return [t0, t1, t2.filter(small)];
}

/** The login form: who logs in may go anywhere, so only the yard's own files and the music. */
export function loginTiers([t0, t1], musicId) {
  return [t0, t1.filter((k) => k === musicId), []];
}

export function evictable({ loaded, keep, inUse, minPx = BIG_PX }) {
  return loaded.filter(({ key, px }) => px >= minPx && isBigFamily(key) && !keep.has(key) && !inUse.has(key))
    .map(({ key }) => key);
}

// A crowded scene on a phone (Gate M, owner option B, 2026-10-31): other players' walk sheets (~10 MB each with a
// hairstyle of their own) took a busy room to ~291 MB, and an iPhone crashed at ~300. With CROWD or more others in the
// scene, tier 1 keeps the way home's backgrounds (the scene's first neighbour) but no other neighbour's, and the
// scene-change free drops them: up to ~80 MB on the street; entering a shop then shows its loading bar. It turns back
// only below CROWD - 1, so one player coming and going does not refetch the shops each time.
export const CROWD = 3;

/** Tier 1 without the big backgrounds not in `spare` (the way home's). */
export function crowdTiers([t0, t1, t2], spare) {
  return [t0, t1.filter((k) => !isBigFamily(k) || spare.has(k)), t2];
}

/** Whether a scene with `others` other players is crowded, given whether it `was`. */
export const crowdedNow = (was, others) => (was ? others >= CROWD - 1 : others >= CROWD);
